import { api } from './api.js';
import type { Conversation, Message } from './types.js';
import { toast } from './ui.js';

interface PushConfig {
  enabled: boolean;
  publicKey: string | null;
}

const preferenceKey = 'relaydesk:operator-notifications';
const deliveryClaimPrefix = 'relaydesk:notified:';
const notificationButton = document.getElementById('notification-button') as HTMLButtonElement;
const notificationDialog = document.getElementById('notification-dialog') as HTMLDialogElement;
const notificationToggle = document.getElementById('notification-toggle') as HTMLButtonElement;
const notificationStatus = document.getElementById('notification-status') as HTMLElement;
const notificationDescription = document.getElementById('notification-description') as HTMLElement;

let config: PushConfig | null = null;
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;
let subscribed = false;
let audioContext: AudioContext | null = null;

export function bindNotificationControls(): void {
  notificationButton.addEventListener('click', () => {
    updateNotificationUi();
    if (typeof notificationDialog.showModal === 'function') notificationDialog.showModal();
    else notificationDialog.setAttribute('open', '');
  });
  notificationToggle.addEventListener('click', () => void toggleNotifications());
  document.getElementById('notification-close')?.addEventListener('click', () => notificationDialog.close());
  document.addEventListener('pointerdown', unlockPreferredSound, { capture: true });
  document.addEventListener('keydown', unlockPreferredSound, { capture: true });
}

export async function initializeNotifications(): Promise<void> {
  try {
    config = await api<PushConfig>('/api/operator/notifications/config');
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      serviceWorkerRegistration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      const existing = await serviceWorkerRegistration.pushManager.getSubscription();
      subscribed = Boolean(existing);
      if (existing && notificationsPreferred()) await persistSubscription(existing);
      if (!existing && notificationsPreferred() && notificationPermission() === 'granted' && canUseMobilePush()) {
        await subscribeForPush();
      }
    }
  } catch (error) {
    console.warn('Notification initialization failed', error);
  }
  updateNotificationUi();
}

export function notifyAboutIncomingMessage(message: Message, conversation: Conversation): void {
  const contactSubmission = message.senderType === 'system' && message.text.startsWith('Контакты отправлены');
  if ((message.senderType !== 'visitor' && !contactSubmission)
    || !notificationsPreferred()
    || !claimSoundDelivery(message.id)) return;
  playIncomingSound();
  if (document.visibilityState === 'visible') {
    toast(contactSubmission
      ? `Новая заявка: ${conversation.visitorName}`
      : `Новое сообщение: ${conversation.visitorName}`);
  }
}

export async function disableNotificationsForLogout(): Promise<void> {
  setNotificationsPreferred(false);
  try {
    const registration = serviceWorkerRegistration ?? await navigator.serviceWorker?.ready;
    const subscription = await registration?.pushManager.getSubscription();
    if (subscription) {
      try {
        await api('/api/operator/notifications/subscriptions', {
          method: 'DELETE',
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });
      } finally {
        await subscription.unsubscribe();
      }
    }
  } catch {
    // Logging out must not be blocked by notification cleanup.
  }
}

async function toggleNotifications(): Promise<void> {
  notificationToggle.disabled = true;
  try {
    const canUpgradeToPush = notificationsPreferred()
      && !subscribed
      && canUseMobilePush()
      && Boolean(config?.enabled)
      && notificationPermission() !== 'denied';
    if (canUpgradeToPush) await enableNotifications();
    else if (notificationsPreferred() || subscribed) await disableNotifications();
    else await enableNotifications();
  } catch (error) {
    console.warn('Notification preference update failed', error);
    toast('Не удалось изменить уведомления. Проверьте настройки браузера.');
  } finally {
    notificationToggle.disabled = false;
    updateNotificationUi();
  }
}

async function enableNotifications(): Promise<void> {
  setNotificationsPreferred(true);
  await unlockSound();
  playIncomingSound(true);

  if (!canUseMobilePush()) {
    toast(isIosDevice()
      ? 'Звук включён. Для push добавьте UltraChat на экран «Домой».'
      : 'Звук включён. Push не поддерживаются этим браузером.');
    return;
  }
  if (!config?.enabled || !config.publicKey) {
    toast('Звук включён. Сервер push пока не настроен.');
    return;
  }

  const permission = Notification.permission === 'default'
    ? await Notification.requestPermission()
    : Notification.permission;
  if (permission !== 'granted') {
    toast('Звук включён, но системные уведомления запрещены в браузере.');
    return;
  }

  await subscribeForPush();
  toast('Звук и системные уведомления включены');
}

async function disableNotifications(): Promise<void> {
  setNotificationsPreferred(false);
  try {
    const registration = serviceWorkerRegistration ?? await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      try {
        await api('/api/operator/notifications/subscriptions', {
          method: 'DELETE',
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });
      } finally {
        await subscription.unsubscribe();
      }
    }
  } catch (error) {
    console.warn('Push unsubscribe failed', error);
  }
  subscribed = false;
  toast('Уведомления выключены');
}

async function subscribeForPush(): Promise<void> {
  if (!config?.publicKey) return;
  serviceWorkerRegistration ??= await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  let subscription = await serviceWorkerRegistration.pushManager.getSubscription();
  subscription ??= await serviceWorkerRegistration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: decodeVapidKey(config.publicKey)
  });
  await persistSubscription(subscription);
  subscribed = true;
}

async function persistSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error('Incomplete Push subscription');
  await api('/api/operator/notifications/subscriptions', {
    method: 'POST',
    body: JSON.stringify({
      endpoint: json.endpoint,
      expirationTime: json.expirationTime ?? null,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }
    })
  });
}

function updateNotificationUi(): void {
  const preferred = notificationsPreferred();
  const fullyEnabled = preferred && subscribed && notificationPermission() === 'granted';
  notificationButton.classList.toggle('is-enabled', fullyEnabled);
  notificationButton.classList.toggle('is-partial', preferred && !fullyEnabled);
  notificationButton.title = fullyEnabled
    ? 'Уведомления включены'
    : preferred ? 'Включён только звук' : 'Уведомления выключены';
  notificationButton.setAttribute('aria-label', notificationButton.title);
  const canUpgradeToPush = preferred
    && !subscribed
    && canUseMobilePush()
    && Boolean(config?.enabled)
    && notificationPermission() !== 'denied';
  notificationToggle.textContent = canUpgradeToPush
    ? 'Включить push'
    : preferred || subscribed ? 'Выключить уведомления' : 'Включить уведомления';

  if (!preferred) {
    notificationStatus.textContent = 'Выключены';
    notificationDescription.textContent = 'Включите звук новых сообщений и системные push-уведомления для этого устройства.';
    return;
  }
  if (fullyEnabled) {
    notificationStatus.textContent = 'Звук и push включены';
    notificationDescription.textContent = 'В открытой операторской прозвучит сигнал, а в фоне уведомление покажет операционная система.';
    return;
  }
  notificationStatus.textContent = 'Звук включён';
  if (isIosDevice() && !isStandalone()) {
    notificationDescription.textContent = 'Чтобы получать push на iPhone: откройте меню «Поделиться», выберите «На экран Домой», запустите UltraChat с новой иконки и включите уведомления ещё раз.';
  } else if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
    notificationDescription.textContent = 'Push заблокированы настройками браузера. Разрешите уведомления для этого сайта, затем включите их здесь снова.';
  } else if (!config?.enabled) {
    notificationDescription.textContent = 'Звуковой сигнал работает. Серверная отправка push ещё не настроена.';
  } else {
    notificationDescription.textContent = 'Звуковой сигнал работает в открытой операторской. Push недоступны в этом браузере.';
  }
}

function canUseMobilePush(): boolean {
  return 'serviceWorker' in navigator
    && 'PushManager' in window
    && typeof Notification !== 'undefined'
    && (!isIosDevice() || isStandalone());
}

function notificationPermission(): NotificationPermission | 'unsupported' {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

function isIosDevice(): boolean {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isStandalone(): boolean {
  const iosNavigator = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || iosNavigator.standalone === true;
}

function notificationsPreferred(): boolean {
  return localStorage.getItem(preferenceKey) !== 'disabled';
}

function setNotificationsPreferred(enabled: boolean): void {
  localStorage.setItem(preferenceKey, enabled ? 'enabled' : 'disabled');
}

function unlockPreferredSound(): void {
  if (notificationsPreferred()) void unlockSound();
}

async function unlockSound(): Promise<void> {
  const AudioContextConstructor = window.AudioContext
    ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextConstructor) return;
  audioContext ??= new AudioContextConstructor();
  if (audioContext.state === 'suspended') await audioContext.resume();
}

function playIncomingSound(preview = false): void {
  if (!audioContext || audioContext.state !== 'running') return;
  const now = audioContext.currentTime;
  const gain = audioContext.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(preview ? 0.035 : 0.11, now + 0.018);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.52);
  gain.connect(audioContext.destination);

  for (const [frequency, delay] of [[740, 0], [988, 0.13]] as const) {
    const oscillator = audioContext.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, now + delay);
    oscillator.connect(gain);
    oscillator.start(now + delay);
    oscillator.stop(now + delay + 0.25);
  }
}

function claimSoundDelivery(messageId: string): boolean {
  const key = `${deliveryClaimPrefix}${messageId}`;
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, String(Date.now()));
    window.setTimeout(() => localStorage.removeItem(key), 10_000);
  } catch {
    // Storage can be unavailable in private contexts; a duplicate sound is preferable to silence.
  }
  return true;
}

function decodeVapidKey(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes.buffer;
}
